import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import SelfEsteemQuiz, { QuizTrigger } from "@/components/site/SelfEsteemQuiz";
import KidsQuiz, { KidsQuizTrigger } from "@/components/site/KidsQuiz";
import { useLang } from "@/lib/i18n";
import { SERVICE_DETAILS, DEFAULT_SERVICE_DETAILS, buildWhatsappLink } from "@/lib/service-details";
import {
  MapPin, Phone, Instagram, Sparkles, Smile, Syringe,
  ArrowRight, Quote, ChevronRight, Lock, Globe, MessageCircle,
} from "lucide-react";

const IMG = {
  hero: "/services/hero.png",
  clinic: "/services/clinic.png",
  about: "/services/about.png",
  smile: "/services/smile.png",
  braces: "/services/brackets.png",
  invisalign: "/services/invisalign.png",
  ortopedia: "/services/ortopedia.png",
  facial: "/services/facial.png",
  acido: "/services/acido.png",
  botox: "/services/botox.png",
};

const SERVICE_IMG = {
  // Ortodoncia
  brackets: IMG.braces,
  invisalign: IMG.invisalign,
  ortopedia: IMG.ortopedia,
  // Medicina Estética — todas imágenes únicas
  toxina: "/services/toxina.png",
  ellanse: "/services/ellanse.png",
  rino: "/services/rino.png",
  labios: "/services/labios.png",
  ojeras: "/services/ojeras.png",
  marcaje: "/services/marcaje.png",
  pomulos: "/services/pomulos.png",
  pdrn: "/services/pdrn.png",
  enzimas: "/services/enzimas.png",
  // Nuevos servicios
  nctf: "/services/nctf.png",
  sculptra: "/services/sculptra.png",
  hydrofacial: "/services/hydrofacial.png",
  limpieza_af: "/services/limpieza_af.png",
  dermapen: "/services/dermapen.png",
  maseteros: "/services/maseteros.png",
  surcos: "/services/surcos.png",
  peeling: "/services/peeling.png",
  aparatologia: "/services/aparatologia.png",
  hollywood: "/services/hollywood.png",
  mounjaro: "/services/mounjaro.png",
  hifu: "/services/hifu.png",
};

export default function Landing() {
  const { t, lang, setLang } = useLang();
  const [gallery, setGallery] = useState([]);
  const [stats, setStats] = useState({ patients_served: 1200, years_experience: 12 });
  const [booking, setBooking] = useState({ name: "", phone: "", email: "", service: t.booking.services[0], date: "", time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [kidsQuizOpen, setKidsQuizOpen] = useState(false);
  const [branch, setBranch] = useState("estetica"); // 'estetica' | 'ortodoncia' — default Estética
  const [activeService, setActiveService] = useState(null); // service object opened in modal

  useEffect(() => {
    api.get("/gallery", { params: { real_only: true } }).then((r) => setGallery(r.data)).catch(() => {});
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Keep booking.service in sync with active language (avoids stale ES label in EN mode)
  useEffect(() => {
    setBooking((b) => ({ ...b, service: t.booking.services[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!booking.name || !booking.phone || !booking.date || !booking.time) {
      toast.error(t.booking.missingMsg);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/appointments", booking);
      toast.success(t.booking.successMsg);
      setBooking({ name: "", phone: "", email: "", service: t.booking.services[0], date: "", time: "", notes: "" });
    } catch {
      toast.error(t.booking.errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLang = () => setLang(lang === "es" ? "en" : "es");

  return (
    <div className="bg-[#FAFAFA] text-[#1A1C1A]" data-testid="landing-page">
      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E6E2DC]" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-9 h-9 rounded-full border border-[#A69076] flex items-center justify-center">
              <span className="font-display text-lg text-[#A69076]">tm</span>
            </div>
            <div className="leading-tight">
              <div className="font-display text-lg text-[#1A1C1A]">Dra. Tania Mendoza</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#5C5E5C]">{t.footer.tagline}</div>
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-9 text-sm text-[#5C5E5C]">
            <a href="#servicios" className="editorial-underline" data-testid="nav-services">{t.nav.services}</a>
            <button type="button" onClick={() => setQuizOpen(true)} className="relative inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#D97757] to-[#C7634A] hover:from-[#1A1C1A] hover:to-[#1A1C1A] text-white px-3.5 h-9 text-[10px] uppercase tracking-[0.15em] transition-all shadow-sm whitespace-nowrap" data-testid="nav-quiz">
              <Sparkles className="w-3 h-3" /> {t.nav.quiz}
              <span className="bg-white text-[#D97757] rounded-full px-1.5 py-0.5 text-[9px] font-bold tracking-wider leading-none">−15%</span>
            </button>
            <a href="#sobre" className="editorial-underline" data-testid="nav-about">{t.nav.about}</a>
            <a href="#galeria" className="editorial-underline" data-testid="nav-gallery">{t.nav.cases}</a>
            <a href="#testimonios" className="editorial-underline" data-testid="nav-testimonials">{t.nav.patients}</a>
            <a href="#contacto" className="editorial-underline" data-testid="nav-contact">{t.nav.contact}</a>
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleLang}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#5C5E5C] hover:text-[#A69076] border border-[#E6E2DC] hover:border-[#A69076] rounded-full px-3 py-1.5 transition-colors"
              data-testid="lang-toggle"
              aria-label="Change language"
            >
              <Globe className="w-3.5 h-3.5" /> {lang === "es" ? "EN" : "ES"}
            </button>
            <Link to="/admin/login" className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#5C5E5C] hover:text-[#A69076]" data-testid="header-admin-link">
              <Lock className="w-3.5 h-3.5" /> {t.nav.admin}
            </Link>
            <a href="#agendar" data-testid="header-cta-book">
              <Button className="rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white px-6 h-10 text-xs uppercase tracking-widest">
                {t.nav.book}
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[#D9CDBC]/30 blur-3xl pointer-events-none" />
        <div className="absolute top-40 -left-32 w-[400px] h-[400px] rounded-full bg-[#D97757]/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F4F0EB] border border-[#E6E2DC] text-[11px] tracking-[0.25em] uppercase text-[#5C5E5C] mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D97757] animate-pulse" /> {t.hero.badge}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tight text-[#1A1C1A]" data-testid="hero-title">
              {t.hero.titlePre}<br />
              <span className="italic text-[#D97757]">{t.hero.titleHighlight}</span>{t.hero.titlePost}
            </h1>
            <p className="mt-8 max-w-xl text-[15px] leading-relaxed text-[#5C5E5C]">
              <span className="text-[#1A1C1A]">{t.hero.subtitleStrong1}</span> {t.hero.subtitleAnd} <span className="text-[#1A1C1A]">{t.hero.subtitleStrong2}</span> {t.hero.subtitleBody} <span className="text-[#1A1C1A]">{t.hero.subtitleStrong3}</span>{t.hero.subtitleEnd}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#agendar" data-testid="hero-cta-book">
                <Button className="rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white h-12 px-7 text-xs uppercase tracking-[0.2em]">
                  {t.hero.cta} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#servicios" className="text-sm text-[#5C5E5C] editorial-underline" data-testid="hero-link-services">
                {t.hero.linkServices}
              </a>
            </div>
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 max-w-xl gap-6">
              <Stat n={t.hero.stat1.n(stats.patients_served)} label={t.hero.stat1.label} />
              <Stat n={t.hero.stat2.n(stats.years_experience)} label={t.hero.stat2.label} />
              <Stat n={t.hero.stat3.n} label={t.hero.stat3.label} />
              <Stat n={t.hero.stat4.n} label={t.hero.stat4.label} />
            </div>
          </div>
          <div className="lg:col-span-5 fade-up-delay-1">
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-[#D9CDBC] to-transparent rounded-[2rem] blur-2xl opacity-50" />
              <div className="relative overflow-hidden rounded-[1.5rem] aspect-[4/5] shadow-xl">
                <img src={IMG.hero} alt="Dra. Tania Mendoza" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 lg:-left-12 bg-white px-6 py-5 rounded-2xl shadow-lg border border-[#E6E2DC] max-w-[220px]">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">{t.hero.certBadge}</div>
                <div className="font-display text-lg text-[#1A1C1A] leading-tight mt-1">{t.hero.university}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section id="servicios" className="py-24 lg:py-32 bg-[#F4F0EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#A69076] mb-4">{t.services.eyebrow}</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl">
                {t.services.title1}<br />{t.services.title2} <em className="text-[#D97757]">{t.services.titleEm}</em>
              </h2>
            </div>
            <p className="max-w-sm text-[15px] text-[#5C5E5C]">{t.services.sub}</p>
          </div>

          {/* Branch tabs: Estética (default) | Ortodoncia */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-white rounded-full p-1.5 border border-[#E6E2DC] shadow-sm" role="tablist" data-testid="branch-tabs">
              <button
                role="tab"
                aria-selected={branch === "estetica"}
                onClick={() => setBranch("estetica")}
                className={`flex items-center gap-2 px-6 sm:px-10 h-11 rounded-full text-xs sm:text-[13px] tracking-[0.18em] uppercase font-medium transition-all ${
                  branch === "estetica"
                    ? "bg-[#1A1C1A] text-white shadow-md"
                    : "text-[#5C5E5C] hover:text-[#1A1C1A]"
                }`}
                data-testid="tab-estetica"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${branch === "estetica" ? "bg-[#D97757]" : "bg-[#D97757]/40"}`} />
                {t.services.tabs.estetica}
              </button>
              <button
                role="tab"
                aria-selected={branch === "ortodoncia"}
                onClick={() => setBranch("ortodoncia")}
                className={`flex items-center gap-2 px-6 sm:px-10 h-11 rounded-full text-xs sm:text-[13px] tracking-[0.18em] uppercase font-medium transition-all ${
                  branch === "ortodoncia"
                    ? "bg-[#1A1C1A] text-white shadow-md"
                    : "text-[#5C5E5C] hover:text-[#1A1C1A]"
                }`}
                data-testid="tab-ortodoncia"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${branch === "ortodoncia" ? "bg-[#7A9180]" : "bg-[#7A9180]/40"}`} />
                {t.services.tabs.ortodoncia}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
            {t.services.list.filter((s) => s.area === branch).map((s) => (
              <article
                key={s.id}
                onClick={() => setActiveService(s)}
                className="group bg-white rounded-2xl overflow-hidden border border-[#E6E2DC] transition-all hover:-translate-y-1 hover:shadow-xl cursor-pointer"
                data-testid={`service-card-${s.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#F4F0EB]">
                  {SERVICE_IMG[s.id] && (
                    <img src={SERVICE_IMG[s.id]} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  )}
                </div>
                <div className="p-7">
                  <div className={`text-[10px] tracking-[0.25em] uppercase mb-2 ${s.area === "estetica" ? "text-[#D97757]" : "text-[#7A9180]"}`}>{t.services.tabs[s.area]}</div>
                  <h3 className="font-display text-2xl text-[#1A1C1A] mb-3">{s.title}</h3>
                  <p className="text-sm text-[#5C5E5C] leading-relaxed">{s.desc}</p>
                  <div className="mt-4 text-[11px] tracking-[0.2em] uppercase text-[#A69076] inline-flex items-center gap-1.5 group-hover:text-[#D97757] transition-colors">
                    Ver detalles <ChevronRight className="w-3 h-3" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FORMACIÓN / CREDENCIALES */}
      <section id="formacion" className="py-24 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#A69076] mb-4">{t.credentials.eyebrow}</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t.credentials.title1}<br /><em className="text-[#A69076]">{t.credentials.title2}</em>
            </h2>
            <p className="mt-6 max-w-xl mx-auto text-[15px] text-[#5C5E5C]">{t.credentials.sub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5">
            {t.credentials.list.map((c, i) => (
              <div key={i} className="flex items-start gap-5 bg-white border border-[#E6E2DC] rounded-2xl p-6 hover:border-[#A69076] transition-colors" data-testid={`credential-${i}`}>
                <div className="shrink-0 w-12 h-12 rounded-full bg-[#F4F0EB] border border-[#D9CDBC] flex items-center justify-center">
                  <span className="font-display text-lg text-[#A69076]">0{i + 1}</span>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">{c.school}</div>
                  <div className="font-display text-lg text-[#1A1C1A] leading-tight">{c.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUIZ AUTOESTIMA + QUIZ INFANTIL */}
      <section id="quiz" className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-6">
          <QuizTrigger onClick={() => setQuizOpen(true)} />
          <KidsQuizTrigger onClick={() => setKidsQuizOpen(true)} />
        </div>
      </section>

      {/* SOBRE */}
      <section id="sobre" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="overflow-hidden rounded-[1.5rem] aspect-[4/5]">
                <img src={IMG.about} alt="Dra. Tania Mendoza" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-10">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#A69076] mb-4">{t.about.eyebrow}</div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.1] tracking-tight max-w-xl">{t.about.title}</h2>
            <p className="mt-7 text-[15px] text-[#5C5E5C] leading-relaxed max-w-xl">{t.about.body}</p>
            <div className="mt-10 grid grid-cols-2 gap-8 max-w-lg">
              {t.about.pillars.map((p) => <Pillar key={p.title} title={p.title} text={p.text} />)}
            </div>
          </div>
        </div>
      </section>

      {/* GALERIA */}
      <section id="galeria" className="py-24 lg:py-32 bg-[#1A1C1A] text-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#A69076] mb-4">{t.gallery.eyebrow}</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl">
                {t.gallery.title1}<br />{t.gallery.title2} <em className="text-[#D9CDBC]">{t.gallery.titleEm}</em>
              </h2>
            </div>
            <p className="max-w-sm text-[15px] text-[#D9CDBC]">{t.gallery.sub}</p>
          </div>

          {(() => {
            const filtered = gallery.filter((g) => g.category === branch);
            if (filtered.length === 0) {
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-placeholder">
                  {[IMG.smile, IMG.facial, IMG.braces].map((src, i) => (
                    <div key={i} className="editorial-photo aspect-[4/5] rounded-2xl bg-[#1A1C1A]">
                      <img src={src} alt="" className="w-full h-full object-cover opacity-60" />
                    </div>
                  ))}
                </div>
              );
            }
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
                {filtered.map((g) => (
                <article key={g.id} className="bg-[#2a2b29] rounded-2xl overflow-hidden border border-[#3a3a3a]" data-testid={`gallery-item-${g.id}`}>
                  <div className="grid grid-cols-2 gap-0.5">
                    <div className="editorial-photo relative aspect-square bg-[#1A1C1A]">
                      <img src={g.before_image} alt={t.gallery.before} className="w-full h-full object-cover" />
                      <span className="badge absolute top-3 left-3 text-[10px] tracking-widest uppercase bg-black/70 text-white px-2.5 py-1 rounded-sm">{t.gallery.before}</span>
                    </div>
                    <div className="editorial-photo relative aspect-square bg-[#1A1C1A]">
                      <img src={g.after_image} alt={t.gallery.after} className="w-full h-full object-cover" />
                      <span className="badge absolute top-3 left-3 text-[10px] tracking-widest uppercase bg-[#A69076] text-[#1A1C1A] px-2.5 py-1 rounded-sm">{t.gallery.after}</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1.5">{t.gallery.cats[g.category] || g.category}</div>
                    <h3 className="font-display text-xl text-[#FAFAFA]">{g.title}</h3>
                    {g.description && <p className="text-xs text-[#a8a9a6] mt-1.5">{g.description}</p>}
                  </div>
                </article>
              ))}
            </div>
            );
          })()}
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section id="testimonios" className="py-24 lg:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-14">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#A69076] mb-4">{t.testimonials.eyebrow}</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl">{t.testimonials.title}</h2>
        </div>
        <div className="relative">
          <div className="flex gap-8 marquee-track" data-testid="testimonials-track">
            {[...t.testimonials.list, ...t.testimonials.list].map((tx, i) => (
              <div key={i} className="min-w-[340px] max-w-[340px] bg-[#F4F0EB] rounded-2xl p-7 border border-[#E6E2DC]">
                <Quote className="w-6 h-6 text-[#A69076] mb-4" />
                <p className="text-[15px] text-[#1A1C1A] leading-relaxed italic font-display text-xl">&ldquo;{tx.text}&rdquo;</p>
                <div className="mt-5 text-xs tracking-[0.2em] uppercase text-[#5C5E5C]">— {tx.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UBICACIÓN / MAPA */}
      <section id="ubicacion" className="py-24 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-stretch">
            <div className="lg:col-span-4 flex flex-col justify-center">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#A69076] mb-4">{t.location.eyebrow}</div>
              <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1A1C1A]">
                {t.location.title1}<br />
                <em className="text-[#D97757]">{t.location.title2}</em>
              </h2>
              <p className="mt-6 text-[15px] text-[#5C5E5C] leading-relaxed">{t.location.body}</p>
              <div className="mt-8 space-y-4 text-sm text-[#1A1C1A]">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#A69076] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">{t.location.address.title}</div>
                    <div className="text-[#5C5E5C] text-[13px] mt-0.5">{t.location.address.line1}</div>
                    <div className="text-[#5C5E5C] text-[13px]">{t.location.address.line2}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#A69076] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">{t.location.phone.title}</div>
                    <a href="tel:+5217712840787" className="text-[#5C5E5C] text-[13px] hover:text-[#A69076] mt-0.5 block">+52 1 771 284 0787</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-4 h-4 text-[#A69076] mt-0.5 shrink-0 flex items-center justify-center text-xs">⏰</span>
                  <div>
                    <div className="font-medium">{t.location.hours.title}</div>
                    <div className="text-[#5C5E5C] text-[13px] mt-0.5">{t.location.hours.weekday}</div>
                    <div className="text-[#5C5E5C] text-[13px]">{t.location.hours.saturday}</div>
                  </div>
                </div>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=El+Pipila+200+Col+Centro+Pachuca+Hidalgo+42000"
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center gap-2 self-start rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white h-11 px-6 text-xs uppercase tracking-[0.2em] transition-colors"
                data-testid="open-google-maps"
              >
                <MapPin className="w-4 h-4" /> {t.location.cta}
              </a>
            </div>

            <div className="lg:col-span-8 rounded-2xl overflow-hidden border border-[#E6E2DC] shadow-sm bg-white">
              <iframe
                title="Ubicación Clínica Dra. Tania Mendoza"
                src="https://www.google.com/maps?q=El+Pipila+200,+Col.+Centro,+42000+Pachuca+de+Soto,+Hidalgo,+M%C3%A9xico&output=embed"
                width="100%"
                height="520"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="location-map"
              />
            </div>
          </div>
        </div>
      </section>

      {/* AGENDAR */}
      <section id="agendar" className="py-24 lg:py-32 bg-[#F4F0EB]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#A69076] mb-4">{t.booking.eyebrow}</div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">{t.booking.title}</h2>
            <p className="mt-6 text-[15px] text-[#5C5E5C] leading-relaxed">{t.booking.body}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A69076] bg-[#FAFAFA] border border-[#E6E2DC] rounded-full px-4 py-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7A9180]" /> {t.booking.payments}
            </div>
            <div className="mt-10 space-y-4 text-sm text-[#5C5E5C]">
              <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-[#A69076]" /> Pachuca, Hidalgo, México</div>
              <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#A69076]" /> +52 1 771 284 0787</div>
              <div className="flex items-center gap-3"><Instagram className="w-4 h-4 text-[#A69076]" /> @dra.taniamendoza</div>
            </div>
          </div>
          <form onSubmit={submitBooking} className="lg:col-span-7 bg-white rounded-2xl p-8 lg:p-10 border border-[#E6E2DC]" data-testid="booking-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t.booking.fields.name}>
                <Input data-testid="booking-name" value={booking.name} onChange={(e) => setBooking({ ...booking, name: e.target.value })} required />
              </Field>
              <Field label={t.booking.fields.phone}>
                <Input data-testid="booking-phone" value={booking.phone} onChange={(e) => setBooking({ ...booking, phone: e.target.value })} required />
              </Field>
              <Field label={t.booking.fields.email}>
                <Input type="email" data-testid="booking-email" value={booking.email} onChange={(e) => setBooking({ ...booking, email: e.target.value })} />
              </Field>
              <Field label={t.booking.fields.service}>
                <Select value={booking.service} onValueChange={(v) => setBooking({ ...booking, service: v })}>
                  <SelectTrigger data-testid="booking-service"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {t.booking.services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t.booking.fields.date}>
                <Input type="date" data-testid="booking-date" value={booking.date} onChange={(e) => setBooking({ ...booking, date: e.target.value })} required />
              </Field>
              <Field label={t.booking.fields.time}>
                <Input type="time" data-testid="booking-time" value={booking.time} onChange={(e) => setBooking({ ...booking, time: e.target.value })} required />
              </Field>
            </div>
            <div className="mt-5">
              <Field label={t.booking.fields.notes}>
                <Textarea rows={4} data-testid="booking-notes" value={booking.notes} onChange={(e) => setBooking({ ...booking, notes: e.target.value })} />
              </Field>
            </div>
            <Button type="submit" disabled={submitting} className="mt-7 w-full sm:w-auto rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white h-12 px-10 text-xs uppercase tracking-[0.2em]" data-testid="booking-submit">
              {submitting ? t.booking.submitting : t.booking.submit} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <a
              href={`https://wa.me/5217712840787?text=${encodeURIComponent(t.booking.whatsappMsg)}`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 sm:mt-7 inline-block sm:ml-3"
              data-testid="booking-whatsapp"
            >
              <Button
                type="button"
                className="w-full sm:w-auto rounded-full bg-[#7A9180] hover:bg-[#5d7a66] text-white h-12 px-10 text-xs uppercase tracking-[0.2em]"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> {t.booking.whatsapp}
              </Button>
            </a>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contacto" className="bg-[#1A1C1A] text-[#D9CDBC] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-4 gap-10">
          <div className="lg:col-span-2">
            <div className="font-display text-3xl text-[#FAFAFA]">Dra. Tania Mendoza</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A69076] mt-2">{t.footer.tagline}</div>
            <p className="mt-6 text-sm leading-relaxed max-w-md">{t.footer.desc}</p>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A69076] mb-4">{t.footer.contactTitle}</div>
            <ul className="space-y-2 text-sm">
              <li>Pachuca, Hidalgo</li>
              <li>+52 1 771 284 0787</li>
              <li>hola@taniamendoza.mx</li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A69076] mb-4">{t.footer.socialTitle}</div>
            <ul className="space-y-2 text-sm">
              <li><a href="https://instagram.com" target="_blank" rel="noreferrer" className="editorial-underline">Instagram</a></li>
              <li><a href="https://facebook.com" target="_blank" rel="noreferrer" className="editorial-underline">Facebook</a></li>
              <li><a href="https://wa.me/5217712840787" target="_blank" rel="noreferrer" className="editorial-underline">WhatsApp</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12 pt-6 border-t border-[#2a2b29] flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#7a7b78]">
          <span>© {new Date().getFullYear()} Clínica Dra. Tania Mendoza. {t.footer.rights}</span>
          <Link to="/admin/login" className="hover:text-[#D9CDBC]" data-testid="footer-admin-link">{t.footer.adminLink}</Link>
        </div>
      </footer>

      <SelfEsteemQuiz open={quizOpen} onOpenChange={setQuizOpen} />
      <KidsQuiz open={kidsQuizOpen} onOpenChange={setKidsQuizOpen} />

      {/* ===== SERVICE DETAIL MODAL ===== */}
      <Dialog open={!!activeService} onOpenChange={(o) => { if (!o) setActiveService(null); }}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-white" data-testid="service-modal">
          {activeService && (() => {
            const d = SERVICE_DETAILS[activeService.id] || DEFAULT_SERVICE_DETAILS;
            const accent = activeService.area === "estetica" ? "#D97757" : "#7A9180";
            const accentBg = activeService.area === "estetica" ? "#FBEEE6" : "#E6F0E8";
            const wa = buildWhatsappLink({ serviceTitle: activeService.title });
            return (
              <div className="grid grid-cols-1 md:grid-cols-[1.05fr_1fr] max-h-[88vh]">
                {/* Image */}
                <div className="relative bg-[#F4F0EB] aspect-square md:aspect-auto md:min-h-[480px]">
                  {SERVICE_IMG[activeService.id] && (
                    <img src={SERVICE_IMG[activeService.id]} alt={activeService.title}
                         className="w-full h-full object-cover" />
                  )}
                  <span
                    className="absolute top-5 left-5 text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full font-medium"
                    style={{ background: accentBg, color: accent }}
                  >
                    {t.services.tabs[activeService.area]}
                  </span>
                </div>

                {/* Info */}
                <div className="flex flex-col overflow-hidden">
                  <DialogHeader className="px-7 pt-7 pb-3 border-b border-[#E6E2DC]">
                    <DialogTitle className="font-display text-3xl text-[#1A1C1A] leading-tight">
                      {activeService.title}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-[#5C5E5C] mt-2 leading-relaxed">
                      {d.long_description || activeService.desc}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="flex-1 overflow-y-auto px-7 py-5 space-y-4 text-sm">
                    {d.ideal_for && (
                      <div className="rounded-lg p-3" style={{ background: accentBg }}>
                        <div className="text-[10px] tracking-[0.25em] uppercase font-medium mb-1" style={{ color: accent }}>
                          Ideal para
                        </div>
                        <div className="text-[#1A1C1A]">{d.ideal_for}</div>
                      </div>
                    )}

                    <DetailRow label="Tiempo de aplicación" value={d.tiempo_aplicacion} accent={accent} />
                    <DetailRow label="Cuándo verás resultado" value={d.visibilidad_resultado} accent={accent} />
                    <DetailRow label="Duración del resultado" value={d.duracion_resultado} accent={accent} />

                    {d.incluye && d.incluye.length > 0 && (
                      <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase mb-2" style={{ color: accent }}>Incluye</div>
                        <ul className="space-y-1.5">
                          {d.incluye.map((i, k) => (
                            <li key={k} className="text-[#5C5E5C] flex items-start gap-2">
                              <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: accent }} />
                              {i}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* CTA footer */}
                  <div className="px-7 py-5 bg-[#FAF7F2] border-t border-[#E6E2DC] space-y-3">
                    <div className="text-center">
                      <div className="inline-block text-[11px] tracking-[0.25em] uppercase font-medium px-3 py-1 rounded-full mb-2"
                           style={{ background: accentBg, color: accent }}>
                        ⭐ Primera valoración
                      </div>
                      <div className="font-display text-2xl text-[#1A1C1A]">
                        <span style={{ color: accent }}>15% de descuento</span>
                      </div>
                      <div className="text-xs text-[#5C5E5C] mt-1">Solo agendando por WhatsApp</div>
                    </div>
                    <a
                      href={wa}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl text-white font-medium transition hover:opacity-90"
                      style={{ background: accent }}
                      data-testid="service-modal-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4" /> Agendar con 15% por WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const DetailRow = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] tracking-[0.25em] uppercase mb-1" style={{ color: accent }}>{label}</div>
    <div className="text-[#1A1C1A]">{value}</div>
  </div>
);

const Stat = ({ n, label }) => (
  <div>
    <div className="font-display text-3xl text-[#1A1C1A]">{n}</div>
    <div className="text-[10px] tracking-[0.25em] uppercase text-[#5C5E5C] mt-1">{label}</div>
  </div>
);

const Pillar = ({ title, text }) => (
  <div>
    <div className="text-[11px] tracking-[0.25em] uppercase text-[#A69076] mb-2">{title}</div>
    <p className="text-sm text-[#5C5E5C] leading-relaxed">{text}</p>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">{label}</Label>
    {children}
  </div>
);
